
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronRight, Home, Search, Archive, HelpCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotFoundRedirect from '@/components/common/NotFoundRedirect';

const NotFoundPage = () => {
  return (
    <>
      <NotFoundRedirect />
      <Helmet>
        <title>Sivua ei löytynyt | 404 - Lainasampo.fi</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <div className="mb-8 flex justify-center">
            <div className="rounded-full bg-amber-100 p-6 inline-flex">
              <AlertTriangle size={48} className="text-amber-600" />
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Sivua ei löytynyt</h1>
          <p className="text-xl text-gray-600 mb-10 max-w-xl mx-auto">
            Valitettavasti etsimääsi sivua ei löydy. Kokeile jotain näistä suosituista sivuista.
          </p>
          
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4 mb-12">
            <Link to="/" className="group">
              <div className="bg-white hover:bg-blue-50 p-6 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors h-full flex flex-col items-center shadow-sm">
                <Home className="h-8 w-8 text-blue-600 mb-4" />
                <span className="font-medium group-hover:text-blue-700">Etusivu</span>
              </div>
            </Link>
            
            <Link to="/lainatuotteet" className="group">
              <div className="bg-white hover:bg-blue-50 p-6 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors h-full flex flex-col items-center shadow-sm">
                <Archive className="h-8 w-8 text-blue-600 mb-4" />
                <span className="font-medium group-hover:text-blue-700">Lainatuotteet</span>
              </div>
            </Link>
            
            <Link to="/kilpailuta" className="group">
              <div className="bg-white hover:bg-blue-50 p-6 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors h-full flex flex-col items-center shadow-sm">
                <Search className="h-8 w-8 text-blue-600 mb-4" />
                <span className="font-medium group-hover:text-blue-700">Kilpailuta laina</span>
              </div>
            </Link>
            
            <Link to="/ukk" className="group">
              <div className="bg-white hover:bg-blue-50 p-6 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors h-full flex flex-col items-center shadow-sm">
                <HelpCircle className="h-8 w-8 text-blue-600 mb-4" />
                <span className="font-medium group-hover:text-blue-700">UKK</span>
              </div>
            </Link>
          </div>
          
          <div className="bg-white p-6 md:p-8 rounded-xl border border-gray-200 shadow-sm text-left">
            <h2 className="text-xl font-bold mb-4">Suosituimmat sivut</h2>
            <ul className="space-y-2">
              {[
                { title: "Kulutusluotto", url: "/lainat/kulutusluotto" },
                { title: "Asuntolaina", url: "/lainat/asuntolaina" },
                { title: "Yhdistelylaina", url: "/lainat/yhdistelylaina" },
                { title: "Autolaina", url: "/lainat/autolaina" },
                { title: "Lainan ottamisen opas", url: "/artikkelit/lainan-ottamisen-opas-2025" }
              ].map((link, i) => (
                <li key={i}>
                  <Link to={link.url} className="text-blue-600 hover:text-blue-800 hover:underline flex items-center">
                    <ChevronRight className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span>{link.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
            
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-gray-600 mb-4">Etkö löydä etsimääsi? Ota yhteyttä asiakaspalveluumme.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild variant="default">
                  <Link to="/kilpailuta">
                    Kilpailuta laina
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/yhteystiedot">
                    Ota yhteyttä
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFoundPage;
